import { ExamPart, ExamPartQuestion, NameCodeResponse, QuestionWithChildren, Subject } from '../model/response';
import { ExamErrorCode, ExamPartErrorCode, ExamPartQuestionErrorCode, QuestionType, UserRole } from '../model/enum';
import {
  ExamImportError,
  ExamImportRequest,
  ExamPartImportRequest,
  ExamPartQuestionImportRequest,
  ExamTemplatePartRequest,
  ExamTemplateQuestionRequest,
  ExamTemplateRequest,
} from '../model/request';
import { UserBaseInfo } from '../interface/Personal';

export function getHoursAndMinutes(totalMinutes: number) {
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return { hours, minutes };
}

export function getMinutesAndSeconds(totalSeconds: number) {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return { minutes, seconds };
}

export function formatDate(date: Date | null | undefined) {
  if (!date) return '';
  const year = date.getFullYear().toString().padStart(4, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  const hour = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');

  return `${hour}:${minutes}-${day}/${month}/${year}`;
}

export function formatDateToDDMMYYYY(dateString?: string) {
  if (!dateString) return '';
  const [year, month, day] = dateString.split('-'); // Split the date string
  return `${day}/${month}/${year}`; // Reformat to DD/MM/YYYY
}

export function mapToSelectItems(baseResults: NameCodeResponse[]) {
  return baseResults.map((baseResult) => ({
    label: baseResult.name,
    value: baseResult.id,
  }));
}

export function preProcessHTML(source: string | null | undefined) {
  return source ? source.replace('<mfenced>', '<mfenced><mo>(</mo>').replace('</mfenced>', '<mo>)</mo></mfenced>') : '';
}

export function isYesNoGroupedQuestion(question: QuestionWithChildren) {
  if (question.type !== 'GROUPED_QUESTION') {
    return false;
  }
  if (question.children.length === 0) {
    return false;
  }
  return !question.children.find((child) => child.type !== 'YES_NO_QUESTION');
}

const processExamPartQuestionsFromExamPart = (store: ExamPartQuestion[], examPart: ExamPart) => {
  const examPartQuestions = examPart.examPartQuestions.sort(
    (e1, e2) => e1.examPartQuestionIndex - e2.examPartQuestionIndex,
  );
  const yesNoExamPartQuestions = examPartQuestions.filter(
    (examPartQuestion) => examPartQuestion.question.type === 'YES_NO_QUESTION',
  );
  const yesNoParentExamPartQuestionIds = yesNoExamPartQuestions.map(
    (yesNoExamPartQuestion) => yesNoExamPartQuestion.question.parent.id,
  );
  store.push(
    ...examPartQuestions.filter((examPartQuestion) => {
      if (yesNoParentExamPartQuestionIds.includes(examPartQuestion.question.id)) {
        return true;
      }
      if (examPartQuestion.question.type === 'GROUPED_QUESTION') {
        return false;
      }
      return examPartQuestion.question.type !== 'YES_NO_QUESTION';
    }),
  );
  examPart.children
    .sort((e1, e2) => e1.examPartIndex - e2.examPartIndex)
    .forEach((child) => {
      processExamPartQuestionsFromExamPart(store, child);
    });
};

export function getAllVisibleExamPartQuestions(examParts: ExamPart[]) {
  const examPartQuestions: ExamPartQuestion[] = [];
  examParts
    .sort((e1, e2) => e1.examPartIndex - e2.examPartIndex)
    .forEach((examPart) => processExamPartQuestionsFromExamPart(examPartQuestions, examPart));
  return examPartQuestions;
}

export function getFirstExamPartQuestion(examParts: ExamPart[]): ExamPartQuestion | null {
  if (examParts.length <= 0 || examParts[0].examPartQuestions.length <= 0) {
    return null;
  }
  if (examParts[0].examPartQuestions[0].question.type !== 'GROUPED_QUESTION') {
    return examParts[0].examPartQuestions[0];
  }
  return examParts[0].examPartQuestions.length > 1 ? examParts[0].examPartQuestions[1] : null;
}

export function processExamPartQuestionIndexMap(examParts: ExamPart[]) {
  const examPartQuestionMap = new Map<string, number>();
  examParts.forEach((examPart) => {
    const examPartQuestions = getAllVisibleExamPartQuestions([examPart]);
    examPartQuestions.forEach((examPartQuestion, index) => examPartQuestionMap.set(examPartQuestion.id, index));
  });
  return examPartQuestionMap;
}

const processExamPartQuestionImportsFromExamPartQuestionImport = (
  store: ExamPartQuestionImportRequest[],
  examPartQuestion: ExamPartQuestionImportRequest,
) => {
  if (examPartQuestion.question.type !== 'GROUPED_QUESTION' && examPartQuestion.question.type !== 'YES_NO_QUESTION') {
    store.push(examPartQuestion);
  }
  examPartQuestion.children.forEach((child) => processExamPartQuestionImportsFromExamPartQuestionImport(store, child));
};

const processExamPartQuestionImportsFromExamPart = (
  store: ExamPartQuestionImportRequest[],
  examPart: ExamPartImportRequest,
) => {
  examPart.examPartQuestions.forEach((examPartQuestion) =>
    processExamPartQuestionImportsFromExamPartQuestionImport(store, examPartQuestion),
  );
  examPart.children.forEach((child) => {
    processExamPartQuestionImportsFromExamPart(store, child);
  });
};

export function getAllVisibleExamPartQuestionImports(examPart: ExamPartImportRequest) {
  const examPartQuestions: ExamPartQuestionImportRequest[] = [];
  processExamPartQuestionImportsFromExamPart(examPartQuestions, examPart);
  return examPartQuestions;
}

export function processExamPartQuestionRequestIndexMap(examParts: ExamPartImportRequest[]) {
  const examPartQuestionMap = new Map<string, number>();
  examParts.forEach((examPart) => {
    const examPartQuestions = getAllVisibleExamPartQuestionImports(examPart);
    examPartQuestions.forEach((examPartQuestion, index) => examPartQuestionMap.set(examPartQuestion.id, index));
  });
  return examPartQuestionMap;
}

export function getDetailLetter(detailIndex: number, questionType: QuestionType) {
  const yesNoDetailLetters = ['a.', 'b.', 'c.', 'd.', 'e.', 'f.', 'g.'];
  const oneChoiceDetailLetters = ['A.', 'B.', 'C.', 'D.', 'E.', 'F.', 'G.'];
  if (questionType === 'YES_NO_QUESTION') {
    return yesNoDetailLetters[detailIndex];
  }
  return oneChoiceDetailLetters[detailIndex];
}

export function checkIsEnglishSubject(subject: Subject | null | undefined) {
  const ENGLISH_SUBJECT_CODE = '09';
  return !!subject && subject.code === ENGLISH_SUBJECT_CODE;
}

function isEmpty(s: any) {
  if (s === null || s === undefined) {
    return true;
  }
  const check = s + '';
  return !check || !check.trim();
}

function isEmptyArray(arr: any[] | null | undefined) {
  return !arr || arr.length === 0;
}

function isNotPositiveInteger(s: any) {
  return !/^\d+$/.test('' + s) || s < 0;
}

export function processErrorFromExamImport(
  examImportRequest: ExamImportRequest,
  examTemplate: ExamTemplateRequest | null,
  subject: Subject | null,
  practice: boolean,
) {
  const isEnglishSubject = checkIsEnglishSubject(subject);
  const examErrorCodes: ExamErrorCode[] = [];
  if (isEmpty(examImportRequest.name)) {
    examErrorCodes.push('MISSING_EXAM_NAME');
  }
  if (!examImportRequest.subject?.id) {
    examErrorCodes.push('MISSING_SUBJECT');
  }
  if (!practice) {
    if (!examImportRequest.duration) {
      examErrorCodes.push('MISSING_DURATION');
    } else if (isNotPositiveInteger(examImportRequest.duration)) {
      examErrorCodes.push('DURATION_SHOULD_POSITIVE');
    }
    if (!examImportRequest.requiredTicketCount) {
      examErrorCodes.push('MISSING_REQUIRED_TICKET_COUNT');
    } else if (isNotPositiveInteger(examImportRequest.requiredTicketCount)) {
      examErrorCodes.push('REQUIRED_TICKET_COUNT_SHOULD_POSITIVE');
    }
    if (examTemplate && examTemplate.examTemplateParts.length > examImportRequest.examParts.length) {
      examErrorCodes.push('MISSING_EXAM_PART');
    } else if (examTemplate && examTemplate.examTemplateParts.length < examImportRequest.examParts.length) {
      examErrorCodes.push('EXCESS_EXAM_PART');
    }
  }
  const examPartErrorMap = new Map<
    string,
    {
      errorCodes: ExamPartErrorCode[];
      examPartQuestionErrorMap: Map<string, ExamPartQuestionErrorCode[]>;
    }
  >();
  const examTemplateParts = examTemplate?.examTemplateParts
    ? [...examTemplate?.examTemplateParts].sort((e1, e2) => e1.partIndex - e2.partIndex)
    : undefined;
  examImportRequest.examParts.forEach((examPart, index) => {
    const examTemplatePart = examTemplateParts ? examTemplateParts[index] : undefined;
    const errors = processErrorFromExamPart(examPart, examTemplatePart, isEnglishSubject, practice);
    examPartErrorMap.set(examPart.id, errors);
    examPart.children?.forEach((child, childIndex) => {
      const childErrors = processErrorFromExamPart(
        child,
        examTemplatePart?.children[childIndex],
        isEnglishSubject,
        practice,
      );
      examPartErrorMap.set(child.id, childErrors);
    });
  });
  return {
    errorCodes: examErrorCodes,
    examPartErrorMap,
  };
}

export function processErrorFromExamPart(
  examPart: ExamPartImportRequest,
  examTemplatePart: ExamTemplatePartRequest | undefined,
  isEnglishSubject: boolean,
  practice: boolean,
) {
  const examPartErrorCodes: ExamPartErrorCode[] = [];
  if (isEmpty(examPart.name)) {
    examPartErrorCodes.push('MISSING_EXAM_PART_NAME');
  }
  if (!practice) {
    if (examTemplatePart && examPart.examPartQuestions.length < examTemplatePart.numberOfQuestions) {
      examPartErrorCodes.push('MISSING_QUESTION');
    }
    if (examTemplatePart && examPart.examPartQuestions.length > examTemplatePart.numberOfQuestions) {
      examPartErrorCodes.push('EXCESS_QUESTION');
    }
  }
  const examPartQuestionErrorMap = new Map<string, ExamPartQuestionErrorCode[]>();
  const examTemplateQuestions = examTemplatePart?.examTemplateQuestions
    ? [...examTemplatePart?.examTemplateQuestions].sort((e1, e2) => e1.questionIndex - e2.questionIndex)
    : undefined;
  examPart.examPartQuestions.forEach((examPartQuestion, index) => {
    const examTemplateQuestion =
      examTemplateQuestions && examTemplateQuestions.length > index ? examTemplateQuestions[index] : undefined;
    const errors = processErrorCodeFromExamPartQuestion(
      examPartQuestion,
      examTemplateQuestion,
      isEnglishSubject,
      practice,
    );
    examPartQuestionErrorMap.set(examPartQuestion.id, errors);
    const examTemplateChildrenQuestions =
      examTemplateQuestion && examTemplateQuestion.children
        ? [...examTemplateQuestion.children].sort((e1, e2) => e1.questionIndex - e2.questionIndex)
        : undefined;
    examPartQuestion.children?.forEach((child, childIndex) => {
      const childErrors = processErrorCodeFromExamPartQuestion(
        child,
        examTemplateChildrenQuestions && examTemplateChildrenQuestions.length > childIndex
          ? examTemplateChildrenQuestions[childIndex]
          : undefined,
        isEnglishSubject,
        practice,
      );
      examPartQuestionErrorMap.set(child.id, childErrors);
    });
  });
  return {
    errorCodes: examPartErrorCodes,
    examPartQuestionErrorMap,
  };
}

export function processErrorCodeFromExamPartQuestion(
  examPartQuestion: ExamPartQuestionImportRequest,
  examTemplateQuestion: ExamTemplateQuestionRequest | undefined,
  isEnglishSubject: boolean,
  practice: boolean,
) {
  const examPartQuestionErrorCodes: ExamPartQuestionErrorCode[] = [];
  const isYesNoGroupedQuestion =
    examPartQuestion.question.type === 'GROUPED_QUESTION' &&
    !!examPartQuestion.question.children?.find((child) => child.type === 'YES_NO_QUESTION');
  if (isEmpty(examPartQuestion.question.type)) {
    examPartQuestionErrorCodes.push('MISSING_TYPE');
  }
  if (examTemplateQuestion && examPartQuestion.question.type !== examTemplateQuestion.defaultQuestionType) {
    examPartQuestionErrorCodes.push('WRONG_TYPE_WITH_EXAM_TEMPLATE');
  }
  if (!isEnglishSubject && isEmpty(examPartQuestion.question.topic)) {
    examPartQuestionErrorCodes.push('MISSING_TOPIC');
  }
  if (
    !practice &&
    (examPartQuestion.question.type === 'ONE_CHOICE_QUESTION' ||
      examPartQuestion.question.type === 'SHORT_QUESTION' ||
      examPartQuestion.question.type === 'TOPIC_QUESTION')
  ) {
    if (!examPartQuestion.mark) {
      examPartQuestionErrorCodes.push('MISSING_MARK');
    } else if (examPartQuestion.mark < 0) {
      examPartQuestionErrorCodes.push('MARK_SHOULD_POSITIVE');
    }
    if (!examPartQuestion.weight) {
      examPartQuestionErrorCodes.push('MISSING_WEIGHT');
    } else if (isNotPositiveInteger(examPartQuestion.weight)) {
      examPartQuestionErrorCodes.push('WEIGHT_SHOULD_POSITIVE');
    }
  }
  if (
    isYesNoGroupedQuestion ||
    (examPartQuestion.question.type !== 'GROUPED_QUESTION' && examPartQuestion.question.type !== 'YES_NO_QUESTION')
  ) {
    if (isEmpty(examPartQuestion.question.code)) {
      examPartQuestionErrorCodes.push('MISSING_QUESTION_CODE');
    }
  }
  if (examPartQuestion.question.type !== 'GROUPED_QUESTION') {
    if (isEmptyArray(examPartQuestion.question.exerciseFormats)) {
      examPartQuestionErrorCodes.push('MISSING_EXERCISE_FORMAT');
    }
    if (!isEnglishSubject && isEmptyArray(examPartQuestion.question.skillTypes)) {
      examPartQuestionErrorCodes.push('MISSING_SKILL_TYPE');
    }
    if (isEmptyArray(examPartQuestion.question.thinkingLevels)) {
      examPartQuestionErrorCodes.push('MISSING_THINKING_LEVEL');
    }
  }
  if (
    examPartQuestion.question.type === 'ONE_CHOICE_QUESTION' ||
    examPartQuestion.question.type === 'SHORT_QUESTION' ||
    examPartQuestion.question.type === 'YES_NO_QUESTION'
  ) {
    if (isEmpty(examPartQuestion.question.rightAnswer)) {
      examPartQuestionErrorCodes.push('MISSING_RIGHT_ANSWER');
    }
  }
  if (examPartQuestion.question.type === 'GROUPED_QUESTION') {
    if (examTemplateQuestion && examPartQuestion.children.length < examTemplateQuestion.numberOfQuestions) {
      examPartQuestionErrorCodes.push('MISSING_CHILDREN');
    } else if (examTemplateQuestion && examPartQuestion.children.length > examTemplateQuestion.numberOfQuestions) {
      examPartQuestionErrorCodes.push('EXCESS_CHILDREN');
    }
  }
  if (examPartQuestion.question.type === 'ONE_CHOICE_QUESTION') {
    const notEmptyExamPartQuestionQuestionDetailSize = examPartQuestion.question.questionDetails.length;
    if (notEmptyExamPartQuestionQuestionDetailSize < 4) {
      examPartQuestionErrorCodes.push('MISSING_QUESTION_DETAIL');
    } else if (notEmptyExamPartQuestionQuestionDetailSize > 4) {
      examPartQuestionErrorCodes.push('EXCESS_QUESTION_DETAIL');
    }
  }
  return examPartQuestionErrorCodes;
}

export function checkIfExamRequestHasAnyError(examImportError: ExamImportError | null) {
  if (!examImportError) {
    return false;
  }
  if (examImportError.errorCodes.length > 0) {
    return true;
  }
  for (let examPartError of Array.from(examImportError.examPartErrorMap.values())) {
    if (examPartError.errorCodes.length > 0) {
      return true;
    }
    for (let examPartQuestionError of Array.from(examPartError.examPartQuestionErrorMap.values())) {
      if (examPartQuestionError.length > 0) {
        return true;
      }
    }
  }
  return false;
}

export function hasPermission(requiredRoles: UserRole[] | undefined | null, user: UserBaseInfo | null | undefined) {
  if (!requiredRoles || requiredRoles.length === 0 || !user) {
    return true;
  }
  return user && user.role && requiredRoles.includes(user.role);
}

export function canPersonalCompile(userRole: UserRole | null | undefined) {
  if (!userRole) return false;
  return ['ROLE_ADMIN', 'ROLE_COMPILER', 'ROLE_MASTER_COMPILER'].includes(userRole);
}

export function canMasterCompile(userRole: UserRole | null | undefined) {
  if (!userRole) return false;
  return ['ROLE_ADMIN', 'ROLE_MASTER_COMPILER'].includes(userRole);
}

export const getContentWithoutBreakLineStart = (originalString: string) => {
  if (originalString.startsWith('<p>') && originalString.endsWith('</p>')) {
    originalString = originalString.substring(3, originalString.length - 4);
  }
  return originalString;
};

export const formatNumberWithCommas = (number?: number): string => {
  return new Intl.NumberFormat().format(number ? number : 0);
};
